import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {Col} from "reactstrap";
import {ReactComponent as Excel} from "../../../../../../../assets/images/export.svg";
import {ReactComponent as ChartIcon} from "../../../../../../../assets/images/products/chart.svg";
import Button from "../../../../../../components/base/button/button";
import Timeline from "../../../../../../components/base/timeline";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {createUUId, dateComparator, formatMoney} from "../../../../../../../core/services/utils/utils";
import ExportProductPricesDialog from "../../../../../../components/dialogs/export-product-prices";
import ProductEntryHistoryDialog from "../../../../../../components/dialogs/product-entry-history";
import Api from "../../../../../../../core/services/api/api";
import PriceHistoryColumn from "./sections/price-history-column";
import {Box, Typography, useTheme} from "@mui/material";
import DateUtils from "../../../../../../../core/services/date-utils";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import {useParams} from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import {detectCircularDependencies, isFormula} from "../../../../../../../core/services/formula-engine";
import {FORMULA_RESOLVED_PRICE_DECIMAL_PLACES, formulaResolvedPriceDiscrepancyTolerance, resolveAllFormulasWithRoundedPrices, roundFormulaResolvedPrice,} from "./formula-resolved-price";
import FormulaTemplateBar, {FormulaPriceLevelsTable} from "./sections/formula-management";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import axios from "axios";
import {Alert} from "@mui/lab";
import ConfigurableInfoBlock from "../../../../../../components/app-specific/configurable-info-block";
import {useDispatch} from "react-redux";
import FormulaDiscrepancyResolutionDialog, {RESOLUTION_CHOICES} from "./sections/formula-discrepancy-dialog";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import FormulaVisibilityToggle from "./sections/formula-visibility-toggle";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";

const ProductInformationPricesView = ({loading: loadingProduct, product}) => {
    const params = useParams()
    const [openExport, setOpenExport] = useState(false);
    const [openChart, setOpenChart] = useState(false);
    const [loadingPrices, setLoadingPrices] = useState(true);
    const [selectedHistoryId, setSelectedHistoryId] = useState(null);
    const [pinnedHistoryIds, setPinnedHistoryIds] = useState([]);
    const [histories, setHistories] = useState([]);
    // Formula state
    const [activeFormulas, setActiveFormulas] = useState([]); // [{priceLevelId, formula}]
    const [savingFormulas, setSavingFormulas] = useState(false);
    const [formulasLoaded, setFormulasLoaded] = useState(false);
    const [discrepancies, setDiscrepancies] = useState([]);
    const [checkedDiscrepanciesForPartId, setCheckedDiscrepanciesForPartId] = useState(null);
    const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
    const [resolvingDiscrepancies, setResolvingDiscrepancies] = useState(false);
    const [applyingFormula, setApplyingFormula] = useState(false);
    const [showFormulas, setShowFormulas] = useState(true);
    const [loadingFormulaVisibility, setLoadingFormulaVisibility] = useState(true);

    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const operatorAccessLevels = useAccessLevels();
    const allowFormulas = operatorAccessLevels?.packages?.lookup?.allowPriceFormulas ?? false;
    const canExportProductPrices = operatorAccessLevels.packages.lookup.exportProductPrices;

    const {highestVersion: getCachedFormulaVisibility, save: saveCachedFormulaVisibility} = useSavedComponentsState({
        name: 'product-prices-formula-visibility',
        version: '1.0.0',
    });

    const partId = useMemo(() => Number(params.id), [params.id]);

    const loading = useMemo(() => loadingPrices || loadingProduct, [loadingProduct, loadingPrices])

    /** Ordered price-level metadata list from the live column. */
    const livePriceLevels = useMemo(() => {
        const live = histories.find(h => h.id === -1);
        return live?.prices?.map(p => p.priceLevel) ?? [];
    }, [histories]);

    /** Current (base) prices keyed by priceLevelId, used as starting point for formula resolution. */
    const basePrices = useMemo(() => {
        const live = histories.find(h => h.id === -1);
        const result = {};
        live?.prices?.forEach(p => {
            result[p.priceLevel.id] = p.price ?? null;
        });
        return result;
    }, [histories]);

    /**
     * formulaMap: { [1-based levelIndex]: formulaString }
     * Built from activeFormulas, only including entries that are actual formula expressions.
     */
    const formulaMap = useMemo(() => {
        const map = {};
        activeFormulas.forEach(({priceLevelId, formula}) => {
            const idx = livePriceLevels.findIndex(pl => pl.id === priceLevelId);
            if (idx !== -1 && formula && isFormula(formula)) {
                map[idx + 1] = formula;
            }
        });
        return map;
    }, [activeFormulas, livePriceLevels]);

    const specialResolvedValues = React.useMemo(() => ({
        'Cost': roundFormulaResolvedPrice(product?.cost ?? 0) ?? 0,
        'Avg Cost': roundFormulaResolvedPrice(product?.avgCost ?? 0) ?? 0,
    }), [product?.cost, product?.avgCost]);

    /**
     * Fully resolved price values and per-level formula errors after running the formula engine.
     * resolvedValues: { priceLevelId: resolvedNumber | null }
     * allFormulaErrors: { priceLevelId: errorString }
     */
    const {resolvedValues, allFormulaErrors, hasFormulaErrors} = useMemo(() => {

        // Build index-keyed base values for the engine (1-based)
        const baseByIndex = {};
        livePriceLevels.forEach((pl, i) => {
            baseByIndex[i + 1] = basePrices[pl.id] ?? null;
        });

        // Circular dependency detection
        const cycles = detectCircularDependencies(formulaMap);
        const circularErrors = {};
        if (cycles.length) {
            cycles.forEach(cycle => {
                cycle.forEach(idx => {
                    const pl = livePriceLevels[idx - 1];
                    if (pl) {
                        const cycleStr = cycle.map(i => `[P${i}]`).join(' → ');
                        circularErrors[pl.id] = `Circular dependency: ${cycleStr}`;
                    }
                });
            });
        }

        // Resolve only non-circular formulas
        const safeFormulaMap = {};
        Object.entries(formulaMap).forEach(([idx, formula]) => {
            const pl = livePriceLevels[parseInt(idx, 10) - 1];
            if (pl && !circularErrors[pl.id]) safeFormulaMap[idx] = formula;
        });


        const {resolvedValues, errors: engineErrors} = resolveAllFormulasWithRoundedPrices(
            safeFormulaMap,
            baseByIndex,
            specialResolvedValues,
            livePriceLevels,
            FORMULA_RESOLVED_PRICE_DECIMAL_PLACES,
        );

        const formulaErrors = {};
        livePriceLevels.forEach((pl, i) => {
            if (engineErrors[i + 1]) formulaErrors[pl.id] = engineErrors[i + 1];
        });

        return {
            resolvedValues,
            allFormulaErrors: {...formulaErrors, ...circularErrors},
            hasFormulaErrors: Object.keys(formulaErrors).length > 0 || Object.keys(circularErrors).length > 0,
        };
    }, [livePriceLevels, formulaMap, specialResolvedValues, basePrices]);

    const selectedHistory = useMemo(() => {
        if (!selectedHistoryId)
            return null;
        return histories.find(e => e?.id === selectedHistoryId);
    }, [selectedHistoryId, histories])

    const pinnedHistories = useMemo(() => {
        return histories?.filter(e => pinnedHistoryIds.includes(e.id)) ?? [];
    }, [pinnedHistoryIds, histories])

    const timelineData = useMemo(() => histories.map((history, index) => ({
        id: history.id,
        content: index === 0
            ? <p className={'content'}>Live Price</p>
            : <div>
                <p className={'content'}>{`${DateUtils.serverToLocal(history.modifiedDateTime).format('ll')}`}</p>
                <p className={'operator'}>{history.operator}</p>
            </div>,
        data: history,
        toggleable: !pinnedHistoryIds.includes(history.id),
    })), [histories, pinnedHistoryIds])

    const timelineSelection = useMemo(() => [
            // inject pinned property used in the column component
            ...(pinnedHistoryIds ?? []),
            selectedHistory?.id
        ].filter(e => !!e),
        [selectedHistory, pinnedHistoryIds])

    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])

    /**
     * Gets the live price levels and previous price levels:
     * - Calls the api for getting the current live price levels
     * - Calls the api for getting the previous price levels
     *
     * * sets the live price as the first pinned history
     * @return {Promise<void>}
     */
    const getPriceHistory = useCallback(async (cancelToken) => {
        setLoadingPrices(true);
        let livePriceLevel;
        let otherPriceLevels = [];
        const priceLevelDict = await getPriceLevelDictionary();
        const getLivePriceLevelsResponse = await Api.getPartLivePriceLevels(partId, cancelToken);
        if (!isMounted())
            return;
        if (getLivePriceLevelsResponse?.resultFlag) {
            livePriceLevel = {
                id: -1,
                operator: null,
                prices: getLivePriceLevelsResponse?.data
                        ?.sort((a, b) => a.priceLevel.levelIndex - b.priceLevel.levelIndex)
                        ?.map(e => ({
                            ...e,
                            priceLevel: {
                                ...e.priceLevel,
                                title: priceLevelDict?.find(d => d.priceLevelId === e.priceLevel?.id)?.title ?? e.priceLevel?.title
                            },
                        }))
                    ?? [],
                modifiedDateTime: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                isFetched: true,
            }
        }
        const previousPriceLevels = await Api.getListOfPreviousPriceLevels(partId);
        if (!isMounted())
            return;
        if (previousPriceLevels?.resultFlag) {
            otherPriceLevels = previousPriceLevels?.data?.map(e => ({
                id: createUUId(),
                date: e?.date,
                operatorId: e?.operatorId,
                modifiedDateTime: e?.date,
                operator: e?.operatorName,
                prices: [],
                isFetched: false,
            })) ?? [];
        }
        const response = otherPriceLevels;
        if (livePriceLevel) {
            response.unshift(livePriceLevel);
        }
        response.sort((a, b) => dateComparator(b.modifiedDateTime, a.modifiedDateTime))
        setHistories(response);
        // pin the live prices
        setPinnedHistoryIds([response[0].id]);
        setSelectedHistoryId(null)
        setLoadingPrices(false);
    }, [partId, getPriceLevelDictionary, isMounted])

    /**
     * Loads the active formulas for the current product from the server.
     * @return {Promise<void>}
     */
    const loadActiveFormulas = useCallback(async (cancelToken) => {
        setFormulasLoaded(false);
        const response = await Api.getProductPriceFormulas(partId, cancelToken);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setActiveFormulas(response.data ?? []);
            setFormulasLoaded(true);
        }
    }, [partId, isMounted]);

    /**
     * With each change in id of the url params:
     * - fetches the price histories of the product from the server.
     * - if formulas are allowed, fetches the active formulas of the product from the server.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        const formulaCancelTokenSource = allowFormulas ? axios.CancelToken.source() : undefined;
        void getPriceHistory(cancelTokenSource.token);
        if (allowFormulas) {
            void loadActiveFormulas(formulaCancelTokenSource.token);
        }
        return () => {
            cancelTokenSource.cancel();
            formulaCancelTokenSource?.cancel();
        }
    }, [allowFormulas, getPriceHistory, loadActiveFormulas])

    /**
     * Loads the cached formula visibility preference (show vs hide formula inputs).
     */
    useEffect(() => {
        if (!allowFormulas) {
            setLoadingFormulaVisibility(false);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoadingFormulaVisibility(true);
            const cached = await getCachedFormulaVisibility();
            if (cancelled || !isMounted()) return;
            if (cached?.data === false || cached?.data === true) {
                setShowFormulas(cached.data);
            }
            setLoadingFormulaVisibility(false);
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [allowFormulas, getCachedFormulaVisibility, isMounted]);

    const handleFormulaVisibilityChange = useCallback((visible) => {
        setShowFormulas(visible);
        void saveCachedFormulaVisibility(visible);
    }, [saveCachedFormulaVisibility]);

    /**
     * Changes the price of the given price entry in the live prices of this product.
     * @param {number} priceLevelId
     * @param {number} newPrice
     * @param {boolean} skipApi - If true, only updates the local state.
     * @return {Promise<{resultFlag: boolean, message?: string} | undefined>}
     */
    const changeLivePrice = useCallback(async (priceLevelId, newPrice, skipApi = false) => {
        const live = histories.find(h => h.id === -1);
        const priceEntry = live?.prices?.find(p => p.priceLevel.id === priceLevelId);
        if (!priceEntry) return {resultFlag: false, message: 'Price level not found in live history.'};

        const minimumPrice = Number(priceEntry?.minimumPrice ?? 0);
        if (newPrice < minimumPrice && !skipApi) {
            return {
                resultFlag: false,
                message: `Price cannot be lower than minimum (${minimumPrice})`,
            };
        }

        if (!skipApi) {
            const response = await Api.updatePartPriceLevel({
                partId: partId,
                priceLevelId: priceLevelId,
                newPrice: newPrice,
            });
            if (!isMounted() || !response?.resultFlag) return response;
        }

        // Update local histories to reflect the change immediately
        setHistories(prevState => prevState.map(e => e.id === -1
            ? {
                ...e,
                prices: e.prices?.map(p => p.priceLevel.id === priceLevelId
                    ? {...p, price: newPrice}
                    : p
                )
            }
            : e
        ));

        return {resultFlag: true};
    }, [partId, histories, isMounted]);

    /**
     * Handles a formula change for a single price level.
     * Validates the formula, checks for circular dependencies, updates local state,
     * and persists the updated formula list to the server.
     *
     * @param {number} priceLevelId
     * @param {string} newFormula
     * @return {Promise<{resultFlag: boolean, message?: string} | undefined>}
     */
    const onFormulaChanged = useCallback(async (priceLevelId, newFormula) => {
        const updatedFormulas = activeFormulas.some(f => f.priceLevelId === priceLevelId)
            ? activeFormulas.map(f => f.priceLevelId === priceLevelId ? {...f, formula: newFormula} : f)
            : [...activeFormulas, {priceLevelId, formula: newFormula}];

        const newActiveFormulas = updatedFormulas.filter(f => isFormula(f.formula));

        // Always update local state so the engine and UI can react immediately
        setActiveFormulas(newActiveFormulas);

        // Re-calculate the potential formula map to check for cycles before committing
        const nextFormulaMap = {};
        newActiveFormulas.forEach(({priceLevelId: pid, formula: f}) => {
            const pl = livePriceLevels.find(p => p.id === pid);
            if (pl && f) {
                nextFormulaMap[pl.levelIndex] = f;
            }
        });

        const cycles = detectCircularDependencies(nextFormulaMap);

        // Only persist to the server if the entire formula set is valid (no cycles)
        if (cycles.length === 0) {
            setSavingFormulas(true);

            // 1. Save the formulas themselves
            const formulaResponse = await Api.setProductPriceFormulas({
                partId: partId,
                formulas: newActiveFormulas,
            });

            // the only reason that we do not care for the check of formulaResponse is because what really matters is actually the price set.
            if (!isMounted()) return formulaResponse;

            // 2. Resolve all values using the engine to get final numbers for commit
            const baseByIndex = {};
            livePriceLevels.forEach((pl, i) => {
                baseByIndex[i + 1] = basePrices[pl.id] ?? null;
            });

            const {resolvedByIndex: nextResolved} = resolveAllFormulasWithRoundedPrices(
                nextFormulaMap,
                baseByIndex,
                specialResolvedValues,
                livePriceLevels,
                FORMULA_RESOLVED_PRICE_DECIMAL_PLACES,
            );

            let priceLevelUpdateResponse;
            // 3. Commit the resolved prices to the server
            // We iterate through all levels and sync those that changed (either by formula or manual entry)
            for (const pl of livePriceLevels) {
                const activeObj = updatedFormulas.find(f => f.priceLevelId === pl.id);
                let newValue = roundFormulaResolvedPrice(nextResolved[pl.levelIndex]);

                // If it's not a formula but a valid number, treat it as a manual price update
                if (activeObj && !isFormula(activeObj.formula)) {
                    const asNum = Number(activeObj.formula);
                    if (!isNaN(asNum)) newValue = roundFormulaResolvedPrice(asNum);
                }

                const currentValue = basePrices[pl.id];

                // If the value is a valid number and differs from current, commit it
                if (newValue !== null && !isNaN(newValue) && newValue !== currentValue) {
                    const response = await changeLivePrice(pl.id, newValue);
                    if (pl.id === priceLevelId) {
                        priceLevelUpdateResponse = response;
                    }
                }
            }
            if (isMounted()) {
                setSavingFormulas(false);
            }

            if (priceLevelUpdateResponse && !priceLevelUpdateResponse.resultFlag) {
                return priceLevelUpdateResponse;
            }
            return formulaResponse;
        }
        // Return a successful flag for the local update, even though server sync is deferred
        return {resultFlag: true, message: 'Local update successful. Resolve cycles to persist.'};
    }, [activeFormulas, livePriceLevels, partId, isMounted, specialResolvedValues, basePrices, changeLivePrice]);

    /**
     * Checks if two formula lists are functionally identical.
     * @param {Array} list1
     * @param {Array} list2
     * @returns {boolean}
     */
    const areFormulasIdentical = useCallback((list1, list2) => {
        if (!list1 && !list2) return true;
        if (!list1 || !list2) return false;
        if (list1.length !== list2.length) return false;

        const map1 = Object.fromEntries(list1.map(f => [f.priceLevelId, f.formula]));
        const map2 = Object.fromEntries(list2.map(f => [f.priceLevelId, f.formula]));

        const keys1 = Object.keys(map1);
        if (keys1.length !== Object.keys(map2).length) return false;

        return keys1.every(id => map1[id] === map2[id]);
    }, []);

    /**
     * Applies a saved formula template's formula list to the active formulas.
     * @param {object | null} newTemplate
     */
    const onFormulaTemplateSelected = useCallback(async (newTemplate) => {
        if (!newTemplate) {
            return true;
        }

        const templateItems = newTemplate.items ?? [];
        if (areFormulasIdentical(templateItems, activeFormulas)) {
            return true;
        }

        const addedOrChanged = templateItems.filter((item) => {
            const active = activeFormulas.find(f => f.priceLevelId === item.priceLevelId);
            return !active || active.formula !== item.formula;
        });

        const removed = activeFormulas.filter((active) => {
            const item = templateItems.find(f => f.priceLevelId === active.priceLevelId);
            return !item;
        }).map(active => ({...active, formula: '(Removed)'}));

        const nonIdenticalFormulas = [...addedOrChanged, ...removed];

        const priceLevelDict = await getPriceLevelDictionary();

        const confirmed = await new Promise((resolve) => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                title: 'Apply Formula Template',
                description: (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                        <Typography variant="body2">
                            Are you sure you want to apply the "{newTemplate.name}" template? This will override your current formulas and trigger a recalculation of affected prices.
                        </Typography>
                        <Box sx={{display: 'flex', gap: 3}}>
                            <Typography variant="body2" fontWeight={600} color={'text.primary'}>
                                Current Cost: {formatMoney(specialResolvedValues["Cost"] ?? 0)}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color={'text.primary'}>
                                Avg Cost: {formatMoney(specialResolvedValues['Avg Cost'] ?? 0)}
                            </Typography>
                        </Box>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant="body2" color={'tertiary.main'}>
                                Current Template's formulas:
                            </Typography>
                            <FormulaPriceLevelsTable
                                items={activeFormulas}
                                priceLevelDict={priceLevelDict}
                            />
                        </Box>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant="body2" fontStyle={'italic'} color={'tertiary.main'} fontWeight={'bold'}>
                                Changes to be applied:
                            </Typography>
                            <FormulaPriceLevelsTable
                                items={nonIdenticalFormulas}
                                priceLevelDict={priceLevelDict}
                                bolded
                            />
                        </Box>
                    </Box>
                ),
                cancelButtonText: "Cancel",
                confirmButtonText: "Apply Template",
                maxWidth: 'xs',
                fullWidth: true,
                onClose: (confirmed) => {
                    resolve(confirmed);
                    return Promise.resolve(true);
                }
            }));
        });

        if (!confirmed) {
            return false;
        }

        setSavingFormulas(true);
        const activeFormulasOnly = templateItems.filter(f => isFormula(f.formula));
        const formulaResponse = await Api.setProductPriceFormulas({
            partId: partId,
            formulas: activeFormulasOnly,
        });
        if (!isMounted()) return false;
        if (formulaResponse?.resultFlag) {
            setActiveFormulas(activeFormulasOnly);
            const formulaMap = {};
            activeFormulasOnly.forEach(({priceLevelId: pid, formula: f}) => {
                const pl = livePriceLevels.find(p => p.id === pid);
                if (pl && f && isFormula(f)) {
                    formulaMap[pl.levelIndex] = f;
                }
            });
            const baseByIndex = {};
            livePriceLevels.forEach(pl => {
                baseByIndex[pl.levelIndex] = basePrices[pl.id] ?? 0;
            });
            const {resolvedByIndex, errors} = resolveAllFormulasWithRoundedPrices(
                formulaMap,
                baseByIndex,
                specialResolvedValues,
                livePriceLevels,
                FORMULA_RESOLVED_PRICE_DECIMAL_PLACES,
            );
            for (const pl of livePriceLevels) {
                const newPrice = roundFormulaResolvedPrice(resolvedByIndex[pl.levelIndex]);
                if (newPrice !== null && newPrice !== undefined && !errors[pl.levelIndex]) {
                    const currentPrice = basePrices[pl.id] ?? 0;
                    if (Math.abs(newPrice - currentPrice) > formulaResolvedPriceDiscrepancyTolerance()) {
                        await changeLivePrice(pl.id, newPrice, false);
                        if (!isMounted())
                            return false;
                    }
                }
            }
            setCheckedDiscrepanciesForPartId(null);
            setSavingFormulas(false);
            return true;
        } else {
            setSavingFormulas(false);
            return false;
        }
    }, [areFormulasIdentical, activeFormulas, getPriceLevelDictionary, partId, isMounted, dispatch, specialResolvedValues, livePriceLevels, basePrices, changeLivePrice]);

    /**
     * Fetches the price history specific for visual charts of the current product with the provided filters.
     *
     * @param {any} filters
     * @return {Promise<Partial<{dateLabel: string, data: [{formatValue: (amount: any, decimalCount?: number, decimal?: string, thousands?: string) => string, data: unknown[] | undefined, label: string}], resultFlag: boolean}>>}
     */
    const getProductPricesForChart = async (filters) => {
        const forApi = {
            ...filters,
            partId: partId,
        }
        const [response, priceLevelDict] = await Promise.all([
            Api.generateReportOfPartPriceLevels(forApi),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return {
                resultFlag: false,
            };
        if (!response?.resultFlag) {
            return response;
        }
        const _data = [
            ...(
                histories[0].prices?.map(e => ({
                    submittedDate: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                    newPrice: e.price,
                    operatorName: "",
                    priceLevel: e.priceLevel,
                }))
                ?? []
            ),
            ...(
                response?.data?.map(e => ({
                    submittedDate: e.submittedDate.split('T')[0].concat(`T${e.submittedTime}`),
                    newPrice: e.newPrice,
                    operatorName: e.operatorName,
                    priceLevel: {
                        ...e.priceLevel,
                        title: priceLevelDict?.find(d => d.priceLevelId === e.priceLevel.id)?.title ?? e.priceLevel?.title,
                    },
                })) ?? []
            )
        ];
        const aggregatedPriceLevels =
            Object.fromEntries(
                Object
                    .entries(
                        _data.reduce((item, obj) => {
                            const key = obj.priceLevel.id;
                            return {...item, [key]: [...(item[key] ?? []), obj]}
                        }, {})
                    )
                    .map(([key, arr]) => [
                        key,
                        arr.map(e => ({
                            date: e.submittedDate,
                            entry: e.newPrice,
                            extra: {
                                operator: e.operatorName ?? '',
                                priceLevel: e.priceLevel,
                            },
                        }))
                    ])
            );
        const options = Array(Object.keys(aggregatedPriceLevels).length).fill(null).map((_, i) => ({
            borderColor: chartColors[i % chartColors.length],
            pointBackgroundColor: chartColors[i % chartColors.length],
            pointBorderColor: chartColors[i % chartColors.length],
            borderWidth: 1,
        }));
        return {
            resultFlag: response?.resultFlag,
            dateLabel: 'Date',
            dateExportOrderIndex: 2,
            data: Object.entries(aggregatedPriceLevels).map(([id, data], index) => ({
                data: data,
                id: Number(id),
                label: data?.find(e => e.extra?.priceLevel?.id === Number(id))?.extra?.priceLevel?.title ?? '',
                formatValue: formatMoney,
                options: options[index],
                exportOrderIndex: index + 2,
            })) ?? [],
            exportSpecific: [
                {
                    label: 'Operator',
                    valueGetter: (e) => e?.operator ?? '',
                    orderIndex: 0,
                }
            ],
        };
    }

    /**
     * Fetches the price history specific for exporting of the current product with the provided filters.
     *
     * @param {any} filters
     * @return {Promise<{resultFlag: boolean}|ApiResponseDs<*|null>|undefined>}
     */
    const getProductPricesForExport = async (filters) => {
        const forApi = {
            ...filters,
            partId: partId,
        }
        const [response, priceLevelDict,] = await Promise.all([
            Api.generateReportOfPartPriceLevels(forApi),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return {resultFlag: false};
        if (response?.data) {
            response.data = response.data.map(e => ({
                ...e,
                priceLevel: {
                    ...e.priceLevel,
                    title: priceLevelDict?.find(d => d.priceLevelId === e.priceLevel.id)?.title ?? e.priceLevel,
                }
            }))
        }
        return response;
    }
    /**
     * Changes the selectedHistory entity with respect to the new selection from the timeline.
     *
     * * if the selectedHistory did not exist, then we know for sure that the user has selected an item, thus place
     * it as the selectedHistory
     * * if the selectedHistory already exists in the selection then we know that the user has either clicked on
     * itself or on a new item, so we replace it
     * * if the selectedHistory did not exist in the selection, then remove the selectedHistory
     * @param {TimelineData[]} selection
     */
    const onTimelineSelectionChanged = (selection) => {
        const notFetchedHistories = selection?.filter(e => !e?.data?.isFetched);
        for (let i = 0; i < notFetchedHistories.length; ++i) {
            const forApi = {
                operatorId: notFetchedHistories[i].data.operatorId,
                submittedDate: notFetchedHistories[i].data.date,
                partId: partId
            }
            Api.getPreviousPriceLevelFullDetails(forApi).then((response) => {
                if (!isMounted())
                    return;
                if (response?.resultFlag)
                    setHistories(prevState => prevState.map((e, _, _histories) => {
                        if (e.id === notFetchedHistories[i].data.id) {
                            const _prices = Object
                                .entries(
                                    response?.data?.reduce((item, obj) => {
                                        const key = obj.priceLevel.levelIndex;
                                        return {...item, [key]: [...(item[key] ?? []), obj]}
                                    }, {})
                                )
                                .map(([index, _arr]) => [parseInt(index), _arr])
                                .sort(([indexA], [indexB]) => indexA - indexB);
                            const indices = _prices.map(([index, _arr]) => index);
                            const __prices = (_histories[0].prices).map(e =>
                                indices.includes(e.priceLevel.levelIndex)
                                    ? _prices.find(([index]) => index === e.priceLevel.levelIndex)[1].map(t => ({
                                        title: e.priceLevel.title,
                                        id: t.id,
                                        hasIncreased: t.newPrice > t.oldPrice,
                                        hasDecreased: t.newPrice < t.oldPrice,
                                        amount: t.newPrice,
                                        prevAmount: t.oldPrice,
                                        date: t.submittedDate.split('T')[0].concat(`T${t.submittedTime}`),
                                    })) :
                                    [{
                                        title: e.priceLevel.title,
                                        id: createUUId(),
                                        hasIncreased: false,
                                        hasDecreased: false,
                                        amount: null,
                                        date: null,
                                    }]
                            )
                            return {
                                ...e,
                                //should be any array of arrays sorted by the levelIndex
                                prices: __prices,
                                isFetched: true
                            };
                        }
                        return e;
                    }));
            });
        }
        if (selectedHistoryId) {
            const selectedFound = selection.find(e => e?.id === selectedHistoryId);
            // 1 is used since the first item is always the live price
            if (selectedFound && selection.length > 1) {
                // another item has been picked or removed since the current one is already in the selection list.
                // we check if the last selection is not the selected history, then we replace it
                setSelectedHistoryId(selection[selection.length - 1].data.id)
            }
            if (!selectedFound && selection.length === pinnedHistoryIds.length) {
                // we know that the selectedHistory was removed since all the remaining ones are pinned
                setSelectedHistoryId(null);
            }
        }
        if (!selectedHistoryId && selection.length > 1) {
            // no history was selected, so pick the last item of the selection as the selectedHistory
            setSelectedHistoryId(selection[selection.length - 1].data.id)
        }
    }
    /**
     * Toggles the pinned state of the given history.
     *
     * * if the given history in the pinned histories, remove it and make it into the selected history only if we
     * do not have one
     * * if the given history is the selected history, make it into the pinnedHistories and remove from selectedHistory
     * @param {any} history
     */
    const toggleHistoryPinnedState = (history) => {
        if (pinnedHistoryIds.includes(history.id)) {
            // remove the pinned history
            setPinnedHistoryIds(prevState => prevState.filter(e => e !== history.id));
            if (!selectedHistoryId) {
                // make the removed pinned history, the selected history
                setSelectedHistoryId(history.id);
            } else if (!(pinnedHistoryIds.length - 1)) {
                setSelectedHistoryId(null);
            }
        }
        if (history.id === selectedHistoryId) {
            // only if the history was selected previously, make it into pinned and remove from selectedHistory
            setPinnedHistoryIds(prevState => [...prevState, history.id])
            setSelectedHistoryId(null);
        }
    }

    /**
     * Calculates the discrepancies between the live prices and the formula outputs
     * ONLY once when the data finishes loading for a specific partId.
     */
    useLayoutEffect(() => {
        if (!allowFormulas || !livePriceLevels.length || checkedDiscrepanciesForPartId === partId) {
            return;
        }
        if (loading || !formulasLoaded) return;
        const discrepancyTolerance = formulaResolvedPriceDiscrepancyTolerance(FORMULA_RESOLVED_PRICE_DECIMAL_PLACES);
        const list = [];
        for (const pl of livePriceLevels) {
            const formulaObj = activeFormulas.find(f => f.priceLevelId === pl.id);
            if (formulaObj && isFormula(formulaObj.formula)) {
                const computed = resolvedValues[pl.id];
                const actual = basePrices[pl.id];
                if (computed !== null && actual !== null && Math.abs(computed - actual) > discrepancyTolerance) {
                    list.push({
                        priceLevel: pl,
                        formula: formulaObj.formula,
                        resolvedValue: computed,
                        actualValue: actual,
                    });
                }
            }
        }
        setDiscrepancies(list);
        setCheckedDiscrepanciesForPartId(partId);
    }, [allowFormulas, livePriceLevels, activeFormulas, resolvedValues, basePrices, checkedDiscrepanciesForPartId, partId, loading, formulasLoaded]);

    /**
     * Handles the resolution of discrepancies by applying the user's choices to either keep the formula (and update the price)
     * @param resolutions
     * @returns {Promise<void>}
     */
    const handleResolveDiscrepancies = async (resolutions) => {
        setResolvingDiscrepancies(true);
        const formulasToKeep = [...activeFormulas];
        const pricesToUpdate = [];

        discrepancies.forEach(d => {
            const choice = resolutions[d.priceLevel.id];
            if (choice === RESOLUTION_CHOICES.KEEP_FORMULA) {
                pricesToUpdate.push({
                    priceLevelId: d.priceLevel.id,
                    newPrice: d.resolvedValue,
                });
            } else if (choice === RESOLUTION_CHOICES.KEEP_PRICE) {
                const index = formulasToKeep.findIndex(f => f.priceLevelId === d.priceLevel.id);
                if (index !== -1) {
                    formulasToKeep.splice(index, 1);
                }
            }
        });

        let requiresHistoryReload = false;

        // 1. Update formulas if any were removed
        const formulasRemoved = formulasToKeep.length !== activeFormulas.length;
        if (formulasRemoved) {
            const formulaResponse = await Api.setProductPriceFormulas({
                partId: partId,
                formulas: formulasToKeep,
            });
            if (formulaResponse?.resultFlag) {
                setActiveFormulas(formulasToKeep);
            }
        }

        // 2. Update prices if any were enforced
        if (pricesToUpdate.length > 0) {
            const promises = pricesToUpdate.map(p => Api.updatePartPriceLevel({
                partId: partId,
                priceLevelId: p.priceLevelId,
                newPrice: p.newPrice,
            }));
            await Promise.all(promises);
            requiresHistoryReload = true;
        }

        if (requiresHistoryReload) {
            void getPriceHistory();
        }

        setResolvingDiscrepancies(false);
        setResolutionDialogOpen(false);
        setDiscrepancies([]);
    };

    return (
        <>
            <div className={'product-prices-view part-info-paper'}>
                <Grid container spacing={0} sx={{height: '100%', width: '100%'}}>
                    <Grid xs={12} md={7} lg={8} xl={9} xxl={10} className={'columns-container'}>
                        <Box component={'section'} sx={{padding: 1, paddingInline: 2, width: '100%'}}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid xs={12} sm={allowFormulas ? true : 6}>
                                    <ConfigurableInfoBlock
                                        title={'Current Cost'}
                                        hideFeedback
                                        showHistoryIcon={false}
                                        showChartIcon={false}
                                        className={'disabled'}
                                        onHistoryIconClicked={() => false}
                                        onChartIconClicked={() => false}
                                        onValueChanged={() => void 0}
                                        inputProps={{
                                            placeholder: 'e.g. 123.123',
                                            type: 'number',
                                            value: product?.cost ?? '',
                                            disabled: true,
                                            size: 'small',
                                        }}
                                    />
                                </Grid>
                                <Grid xs={12} sm={allowFormulas ? true : 6}>
                                    <ConfigurableInfoBlock
                                        title={'Average Cost'}
                                        className={'disabled'}
                                        hideFeedback
                                        showHistoryIcon={false}
                                        showChartIcon={false}
                                        onHistoryIconClicked={() => false}
                                        onChartIconClicked={() => false}
                                        onValueChanged={() => void 0}
                                        inputProps={{
                                            placeholder: 'e.g. 123.123',
                                            type: 'number',
                                            value: product?.avgCost ?? '',
                                            disabled: true,
                                            size: 'small',
                                        }}
                                    />
                                </Grid>
                                {
                                    allowFormulas &&
                                    <Grid
                                        xs={12}
                                        sm={'auto'}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: {xs: 'flex-start', sm: 'flex-end'},
                                            alignItems: 'center',
                                        }}
                                    >
                                        <FormulaVisibilityToggle
                                            showFormulas={showFormulas}
                                            onChange={handleFormulaVisibilityChange}
                                            disabled={loading || loadingFormulaVisibility || (discrepancies.length > 0)}
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </Box>
                        <Box className={'product-prices-body'}>
                            <Box className={'price-history-column-container'}>
                                {
                                    loading
                                        ? Array(3).fill(null).map((_, e) => (
                                            <Col key={e} xs={12} md={6} xl={4} className={'price-history-column'}>
                                                {
                                                    Array(15).fill(null).map((_, e) => (
                                                        <div key={e} className={'loading normal mb-4 pr-5'}>
                                                            <div/>
                                                        </div>
                                                    ))
                                                }
                                            </Col>
                                        ))
                                        : [...pinnedHistories, selectedHistory]
                                            .filter(e => e)
                                            .map((priceHistory, index, array) => (
                                                <PriceHistoryColumn
                                                    key={priceHistory.modifiedDateTime}
                                                    priceHistory={priceHistory}
                                                    getProductPricesForChart={getProductPricesForChart}
                                                    getProductPricesForExport={getProductPricesForExport}
                                                    pinned={index < (!!selectedHistory ? array.length - 1 : array.length)}
                                                    live={index === 0}
                                                    loading={index === 0 ? false : !(selectedHistory?.isFetched ?? true)}
                                                    togglePinnedState={toggleHistoryPinnedState}
                                                    partNo={product?.partNo ?? ''}
                                                    partId={partId}
                                                    activeFormulas={activeFormulas}
                                                    resolvedValues={resolvedValues}
                                                    specialResolvedValues={specialResolvedValues}
                                                    allFormulaErrors={allFormulaErrors}
                                                    changePrice={allowFormulas ? changeLivePrice : changeLivePrice}
                                                    onFormulaChanged={allowFormulas ? onFormulaChanged : null}
                                                    allowFormulas={allowFormulas}
                                                    showFormulas={showFormulas}
                                                    disableInteractions={discrepancies.length > 0 && allowFormulas}
                                                />
                                            ))
                                }
                            </Box>
                            {
                                !loading && (discrepancies.length > 0 || applyingFormula) &&
                                <Box sx={[
                                    {
                                        position: 'absolute',
                                        zIndex: 100,
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255,255,255,0.8)',
                                    },
                                    (theme) => theme.applyStyles('dark', {
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                    }),
                                ]}>
                                    <Box sx={{mt: 2, px: 2, maxWidth: 700}}>
                                        {
                                            !applyingFormula &&
                                            discrepancies.length > 0 &&
                                            <Alert
                                                severity="error"
                                                action={<Button color="primary" size="small" onClick={() => setResolutionDialogOpen(true)}>Resolve</Button>}
                                                sx={{
                                                    backgroundColor: 'error.light',
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={600}>
                                                    Formula Discrepancies Detected!
                                                </Typography>
                                                <Typography variant="body2">
                                                    Your live prices have drifted from the active formulas (likely due to external cost changes or manual updates).
                                                    You must resolve these discrepancies before editing prices.
                                                </Typography>
                                            </Alert>
                                        }
                                        {
                                            applyingFormula &&
                                            <LoadingLottieAnimation width={'80%'}/>
                                        }
                                    </Box>
                                </Box>
                            }
                        </Box>
                    </Grid>
                    <Grid xs={12} md={5} lg={4} xl={3} xxl={2} className={'timeline-container'}>
                        <Box sx={{mb: 2, px: 1}}>
                            {
                                allowFormulas &&
                                <FormulaTemplateBar
                                    formulaList={activeFormulas}
                                    onTemplateSelected={onFormulaTemplateSelected}
                                    loading={savingFormulas}
                                    disabled={loading}
                                    onApplyingTemplateAsCurrentFormulaState={setApplyingFormula}
                                />
                            }
                            <Box sx={{mb: 2}}>
                                {
                                    allowFormulas && hasFormulaErrors &&
                                    <Alert variant={'filled'} severity={'error'}>
                                        <Typography variant="body2" fontWeight={600}>
                                            Formula errors detected. Please resolve all errors to ensure correct price calculations.
                                        </Typography>
                                    </Alert>
                                }
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: 2, position: 'sticky', top: 0, zIndex: 1, px: 1, alignItems: 'center', justifyContent: 'center'}}>
                            <Button
                                disabled={!!loading}
                                variant={'outlined'}
                                color={'primary'}
                                startIcon={<ChartIcon style={{transform: 'scale(0.8)'}}/>}
                                onClick={() => setOpenChart(true)}
                            >
                                {loading ? 'Loading' : "Show on Chart"}
                            </Button>
                            {
                                canExportProductPrices &&
                                <Button
                                    disabled={!!loading}
                                    variant={'outlined'}
                                    color={'primary'}
                                    startIcon={<Excel/>}
                                    onClick={() => setOpenExport(true)}
                                >
                                    {loading ? 'Loading' : "Convert to Excel"}
                                </Button>
                            }
                        </Box>
                        <Timeline
                            title={'Time Line'}
                            multiple
                            selection={timelineSelection}
                            data={timelineData}
                            onSelectionChanged={onTimelineSelectionChanged}
                        />
                    </Grid>
                </Grid>
            </div>
            {
                canExportProductPrices &&
                <ExportProductPricesDialog
                    open={openExport}
                    close={() => setOpenExport(false)}
                    partNo={product?.partNo ?? ''}
                    partId={product?.id}
                    livePrices={histories[0]?.prices?.map(p => ({
                        ...p,
                        price: resolvedValues[p.priceLevel.id] ?? p.price
                    })) ?? []}
                />
            }
            <ProductEntryHistoryDialog
                title={"Product Prices"}
                open={openChart}
                close={() => setOpenChart(false)}
                getHistory={getProductPricesForChart}
                hideExport
            />
            <FormulaDiscrepancyResolutionDialog
                open={resolutionDialogOpen}
                onClose={() => setResolutionDialogOpen(false)}
                discrepancies={discrepancies}
                onSubmit={handleResolveDiscrepancies}
                loading={resolvingDiscrepancies}
            />
        </>
    )
}
export default ProductInformationPricesView;
